import { useMemo, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { andFilters, exportQueryOf, resolveFormattingLocale, useListSearch } from '@eis/core';
import {
  InfiniteDataGrid,
  ExportButton,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
  useGridExportColumns,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/group-policies';
import { toGroupPolicyRows } from '../model/types';
import { useGroupPolicyColumns } from './useGroupPolicyColumns';

/**
 * Gruppen-Policen — the operations persona's entry point (SDD-017 §9).
 *
 * Search, filters, order and paging are the SERVICE's: the status tab and the property filter
 * become one `filters` value, the rows arrive page by page as the reader scrolls (opaque tokens,
 * the cut pinned by the first page), and everything a reader chose is in the URL — a filtered
 * list is a link. There is no page control and no cursor stack: the list is one scroll.
 *
 * No version vocabulary appears anywhere on this surface (SSOT I-6): the Group Policy is the
 * operations persona's object, and configuration lives under Produkte.
 *
 * The status filter is a row of pill tabs (EIS Design System, prompt 24 Phase 24.4). The tabs
 * carry no counts: a count of the loaded rows would be a claim about them, not the tenant.
 */
export const groupPolicyStatusTabs = ['all', 'active', 'in-negotiation', 'terminated'] as const;
export type GroupPolicyStatusTab = (typeof groupPolicyStatusTabs)[number];

const STATUS_OF_TAB: Readonly<
  Record<GroupPolicyStatusTab, 'ACTIVE' | 'IN_NEGOTIATION' | 'TERMINATED' | undefined>
> = {
  all: undefined,
  active: 'ACTIVE',
  'in-negotiation': 'IN_NEGOTIATION',
  terminated: 'TERMINATED',
};

const SORT_KEYS: Readonly<Record<string, string>> = {
  code: 'code',
  partnerName: 'holderName',
  status: 'status',
  validFrom: 'validFrom',
};

export function GroupPolicyList(): ReactNode {
  const { t, i18n } = useTranslation(['insurance', 'patterns']);
  const navigate = useNavigate();
  const url = useListSearch();
  const statusTab: GroupPolicyStatusTab = groupPolicyStatusTabs.includes(
    url.raw.status as GroupPolicyStatusTab,
  )
    ? (url.raw.status as GroupPolicyStatusTab)
    : 'all';
  const status = STATUS_OF_TAB[statusTab];

  const catalog = useFilterCatalog('groupPolicies');
  const list = useInfiniteList('groupPolicies', {
    ...url.params,
    filters: andFilters(status === undefined ? undefined : `status==${status}`, url.params.filters),
  });
  const rows = useMemo(() => toGroupPolicyRows(list.items), [list.items]);
  const columns = useGroupPolicyColumns(resolveFormattingLocale(i18n.language));
  const exportColumns = useGridExportColumns(columns);
  const filtered =
    statusTab !== 'all' || (url.params.filters ?? '') !== '' || (url.params.search ?? '') !== '';

  const header = (
    <PageHeader
      title={t('groupPolicies.title')}
      description={t('groupPolicies.subtitle')}
      actions={
        <Stack direction="row" spacing={1}>
          <ExportButton
            service="insurance"
            permission={p.exports.create}
            rowCount={list.isLoading ? undefined : rows.length}
            buildRequest={(format) => ({
              collection: 'group-policies',
              query: exportQueryOf(
                {
                  filters: andFilters(
                    status === undefined ? undefined : `status==${status}`,
                    url.params.filters,
                  ),
                  search: url.params.search,
                  order: url.params.order,
                },
                list.cut,
              ),
              columns: exportColumns,
              format,
              label: t('groupPolicies.title'),
            })}
          />
          <RequirePermission anyOf={[p.groupPolicies.create]}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                void navigate({ to: '/insurance/group-policies/new' });
              }}
            >
              {t('groupPolicies.create')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  const tabs = (
    <Tabs
      value={statusTab}
      onChange={(_event, next: GroupPolicyStatusTab) => {
        url.set({ status: next === 'all' ? undefined : next });
      }}
      aria-label={t('groupPolicies.statusTabs.label')}
    >
      {groupPolicyStatusTabs.map((tab) => {
        const tabStatus = STATUS_OF_TAB[tab];
        return (
          <Tab
            key={tab}
            value={tab}
            label={
              tabStatus === undefined
                ? t('groupPolicies.statusTabs.all')
                : t(`groupPolicies.status.${tabStatus}`)
            }
          />
        );
      })}
    </Tabs>
  );

  return (
    <PageLayout header={header} subHeader={tabs}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
            placeholder={t('groupPolicies.search')}
          />
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('groupPolicies.title')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            showCut
            errorDescription={t('groupPolicies.error.list')}
            emptyTitle={filtered ? undefined : t('groupPolicies.empty.title')}
            emptyDescription={
              filtered ? t('groupPolicies.empty.filtered') : t('groupPolicies.empty.description')
            }
            emptyAction={
              filtered ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    url.set({ status: undefined, filters: '', search: '' });
                  }}
                >
                  {t('patterns:actions.clearFilters')}
                </Button>
              ) : undefined
            }
            onRowClick={(row) => {
              if (row.code === '') return;
              void navigate({
                to: '/insurance/group-policies/$groupPolicyId',
                params: { groupPolicyId: row.code },
              });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}
